/** «Бухгалтерия → Расходы»: the expense form's rules (src/core/accounting/service.py add_expense,
 *  docs/acquisition.md §4) — the rate a currency needs, the acquisition channel / campaign of a
 *  marketing expense and the request body. Pure, no I/O; money stays a decimal string. */

import { mulDecimal, parseDecimal } from '@/lib/num'

export const MARKETING = 'marketing'
/** «Пополнение провайдеров»: recorded as a provider ADVANCE, not an expense (owner 2026-10-22). */
export const PROVIDERS = 'providers'
/** An advance is entered in these (the provider bills in USD). */
export const ADVANCE_CURRENCIES = ['USDT', 'USD', 'RUB']
const RECEIPT_MAX = 2000

/** One row of GET /accounting/expenses. */
export interface ExpenseRow {
  id: number
  spent_on: string
  category: string
  amount: string
  currency: string
  rate_rub: string
  amount_rub: string
  note: string | null
  created_by?: number | null
  acq_channel_id?: number | null
  acq_campaign_id?: number | null
  /** present when the channel / campaign is set */
  acq_channel_name?: string | null
  acq_campaign_name?: string | null
  acq_campaign_code?: string | null
  /** a link to the receipt / invoice */
  receipt_url?: string | null
  /** storno: set on the reversal row (negative amount) */
  reverses_id?: number | null
  /** storno: set on the reversed row */
  reversed_by_id?: number | null
}

export interface ExpensesResponse {
  items: ExpenseRow[]
  /** rows of the filter (the list is paged) */
  total?: number
  limit?: number
  offset?: number
  categories: string[]
  /** the category that records a provider advance, and the providers to pick from */
  advance_category?: string
  advance_providers?: Record<string, string>
  currencies: string[]
  /** EUR: no feed, the admin types RUB per 1 unit (required) */
  rate_required_currencies?: string[]
  /** USDT / USD: the USDT/RUB feed by default, a typed rate wins */
  rate_optional_currencies?: string[]
}

export interface ExpenseMeta {
  rateRequired: readonly string[]
  rateOptional: readonly string[]
}

export const DEFAULT_EXPENSE_META: ExpenseMeta = { rateRequired: ['EUR'], rateOptional: ['USDT', 'USD'] }

export function expenseMeta(r: ExpensesResponse | undefined): ExpenseMeta {
  return {
    rateRequired: r?.rate_required_currencies ?? DEFAULT_EXPENSE_META.rateRequired,
    rateOptional: r?.rate_optional_currencies ?? DEFAULT_EXPENSE_META.rateOptional,
  }
}

export type RateMode = 'none' | 'required' | 'optional'

/** RUB: no rate; EUR: the admin must type it; USDT / USD: optional (the feed otherwise). */
export function rateMode(currency: string, meta: ExpenseMeta): RateMode {
  if (meta.rateRequired.includes(currency)) return 'required'
  if (meta.rateOptional.includes(currency)) return 'optional'
  return 'none'
}

/** A RUB provider top-up takes an optional USDT/RUB rate (it becomes USD); else rateMode. */
export function draftRateMode(d: Pick<ExpenseDraft, 'category' | 'currency'>, meta: ExpenseMeta): RateMode {
  if (d.category === PROVIDERS && d.currency === 'RUB') return 'optional'
  return rateMode(d.currency, meta)
}

export interface ExpenseDraft {
  spent_on: string
  category: string
  amount: string
  currency: string
  /** RUB per 1 unit of the currency, as typed */
  rate: string
  note: string
  /** acquisition channel id ('' = none); marketing only */
  channelId: string
  /** acquisition campaign id ('' = none); marketing only — implies its channel */
  campaignId: string
  /** «Пополнение провайдеров» only: the provider topped up */
  provider?: string
  /** optional http(s) link to the receipt */
  receiptUrl?: string
}

export interface CampaignOption {
  id: number
  channel_id: number
  name: string
  code: string
}

/** Category changed: channel and campaign only belong to «Маркетинг». */
export function withCategory(d: ExpenseDraft, category: string): ExpenseDraft {
  return category === MARKETING ? { ...d, category } : { ...d, category, channelId: '', campaignId: '' }
}

/** Currency changed: a typed rate is kept only when the new currency takes one. */
export function withCurrency(d: ExpenseDraft, currency: string, meta: ExpenseMeta): ExpenseDraft {
  return { ...d, currency, rate: rateMode(currency, meta) === 'none' ? '' : d.rate }
}

/** Channel picked: a campaign of another channel is dropped. */
export function withChannel(d: ExpenseDraft, channelId: string, campaigns: readonly CampaignOption[]): ExpenseDraft {
  const camp = campaigns.find((c) => String(c.id) === d.campaignId)
  const keep = camp !== undefined && String(camp.channel_id) === channelId
  return { ...d, channelId, campaignId: keep ? d.campaignId : '' }
}

/** Campaign picked: its channel is set with it (the server checks they match). */
export function withCampaign(d: ExpenseDraft, campaignId: string, campaigns: readonly CampaignOption[]): ExpenseDraft {
  const camp = campaigns.find((c) => String(c.id) === campaignId)
  return { ...d, campaignId, channelId: camp ? String(camp.channel_id) : d.channelId }
}

export type ExpenseField = 'amount' | 'rate' | 'spent_on' | 'campaignId' | 'provider' | 'receiptUrl' | 'currency'

/** An expense row is a storno (negative) or was reversed: the word for the table. */
export function stornoState(e: Pick<ExpenseRow, 'reverses_id' | 'reversed_by_id'>): 'storno' | 'reversed' | null {
  if (e.reverses_id != null) return 'storno'
  if (e.reversed_by_id != null) return 'reversed'
  return null
}

/** '' or an http(s) link without spaces (what the server accepts). */
export function receiptError(url: string): string | undefined {
  const u = url.trim()
  if (!u) return undefined
  if (u.length > RECEIPT_MAX) return 'Ссылка слишком длинная'
  if (!/^https?:\/\/\S+$/i.test(u)) return 'Ссылка должна начинаться с https:// и быть без пробелов'
  return undefined
}

export type ExpenseResult =
  | { ok: true; body: Record<string, unknown> }
  | { ok: false; errors: Partial<Record<ExpenseField, string>> }

/** The POST /accounting/expenses body, or the errors under the fields. */
export function expenseBody(d: ExpenseDraft, meta: ExpenseMeta, campaigns: readonly CampaignOption[] = []): ExpenseResult {
  const errors: Partial<Record<ExpenseField, string>> = {}
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.spent_on)) errors.spent_on = 'Укажите дату расхода'
  const amount = parseDecimal(d.amount)
  if (amount === undefined || amount === null || !/[1-9]/.test(amount)) errors.amount = 'Сумма: число больше нуля, например 1 500 или 12,5'
  const mode = draftRateMode(d, meta)
  let rate: string | null = null
  if (mode !== 'none') {
    const r = parseDecimal(d.rate)
    if (r === undefined || (r !== null && !/[1-9]/.test(r))) errors.rate = 'Курс: число больше нуля — сколько рублей стоит 1 единица'
    else if (r === null && mode === 'required') errors.rate = `Для ${d.currency} курс обязателен: сколько рублей стоит 1 ${d.currency}`
    else rate = r
  }
  const marketing = d.category === MARKETING
  const channelId = marketing && /^\d+$/.test(d.channelId) ? Number(d.channelId) : null
  const campaignId = marketing && /^\d+$/.test(d.campaignId) ? Number(d.campaignId) : null
  if (campaignId !== null && channelId !== null) {
    const camp = campaigns.find((c) => c.id === campaignId)
    if (camp && camp.channel_id !== channelId) errors.campaignId = 'Кампания относится к другому каналу'
  }
  const advance = d.category === PROVIDERS
  if (advance && !(d.provider ?? '').trim()) errors.provider = 'Укажите, какого провайдера пополнили'
  if (advance && !ADVANCE_CURRENCIES.includes(d.currency)) errors.currency = 'Пополнение — в USDT, USD или рублях'
  const receipt = receiptError(d.receiptUrl ?? '')
  if (receipt) errors.receiptUrl = receipt
  if (Object.keys(errors).length > 0) return { ok: false, errors }
  const body: Record<string, unknown> = {
    spent_on: d.spent_on,
    category: d.category,
    amount,
    currency: d.currency,
    note: d.note.trim() || null,
  }
  if (rate !== null) body.rate_rub = rate
  if (channelId !== null) body.acq_channel_id = channelId
  if (campaignId !== null) body.acq_campaign_id = campaignId
  if (advance) body.provider = (d.provider ?? '').trim()
  if ((d.receiptUrl ?? '').trim()) body.receipt_url = (d.receiptUrl ?? '').trim()
  return { ok: true, body }
}

/** «≈ 1 234,56 ₽» preview: amount × rate, kopecks half up, the rate first rounded to 6
 *  decimals — what the server stores (q(amount × rate), RATE_STEP). null when either is
 *  missing or invalid. */
export function rubPreview(amount: string, rate: string | null): string | null {
  const a = parseDecimal(amount)
  if (a === null || a === undefined) return null
  if (rate === null) return null
  const r = parseDecimal(rate)
  if (r === null || r === undefined) return null
  return mulDecimal(a, mulDecimal(r, '1', 6), 2)
}

/** «Telegram Ads, кампания tgads2609» / «Telegram Ads» / null for the table's source column. */
export function expenseSource(e: ExpenseRow): string | null {
  if (e.acq_campaign_id != null) {
    const camp = e.acq_campaign_name ?? `#${e.acq_campaign_id}`
    const code = e.acq_campaign_code ? ` (${e.acq_campaign_code})` : ''
    return `${e.acq_channel_name ?? 'Канал'}: ${camp}${code}`
  }
  if (e.acq_channel_id != null) return e.acq_channel_name ?? `Канал #${e.acq_channel_id}`
  return null
}
